'use client'
import React, { useState, useEffect } from 'react'
import { Search, Eye, Users, Fingerprint, AlertTriangle, CheckCircle, XCircle, Calendar, DollarSign } from 'lucide-react'
import { socket } from '../Containers/MainContainer'

function PlutoFingerDet({ second_page }) {
  const [users, setUsers] = useState([])
  const [rank, setRank] = useState(0)
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [sortBy, setSortBy] = useState('id')
  const [sortOrder, setSortOrder] = useState('asc')

  useEffect(() => {
    socket.on('response_get_admin_fpdetails_users', (data) => {
      setUsers(data.users)
      setRank(data.rank)
      setLoading(false)
    })

    socket.emit('getFPDetailsAdmin', {
      fp: second_page,
    })
  }, [second_page])

  // Filter and sort users
  const filteredUsers = users.filter(user =>
    user.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.id?.toString().includes(searchTerm) ||
    user.country?.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const sortedUsers = [...filteredUsers].sort((a, b) => {
    let aVal = a[sortBy]
    let bVal = b[sortBy]

    if (typeof aVal === 'string') {
      aVal = aVal.toLowerCase()
      bVal = bVal.toLowerCase()
    }

    if (sortOrder === 'asc') {
      return aVal > bVal ? 1 : -1
    } else {
      return aVal < bVal ? 1 : -1
    }
  })

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setSortBy(column)
      setSortOrder('asc')
    }
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount)
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  const getBannedStatus = (banned) => {
    if (banned === 'True') {
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#f85149' }}>
          <XCircle style={{ height: '16px', width: '16px' }} />
          <span>Banned</span>
        </div>
      )
    } else {
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#3fb950' }}>
          <CheckCircle style={{ height: '16px', width: '16px' }} />
          <span>Active</span>
        </div>
      )
    }
  }

  const StatCard = ({ icon: Icon, title, value, color = "blue" }) => {
    const colorMap = {
      blue: { border: '#58a6ff', bg: 'rgba(88,166,255,0.15)', text: '#58a6ff' },
      green: { border: '#3fb950', bg: 'rgba(63,185,80,0.15)', text: '#3fb950' },
      red: { border: '#f85149', bg: 'rgba(248,81,73,0.15)', text: '#f85149' },
      purple: { border: '#a855f7', bg: 'rgba(168,85,247,0.15)', text: '#a855f7' },
    }
    const c = colorMap[color] || colorMap.blue
    return (
      <div style={{ background: '#161b22', borderRadius: '12px', padding: '16px', border: '1px solid #21262d', borderLeft: `3px solid ${c.border}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{ padding: '8px', borderRadius: '8px', background: c.bg }}>
            <Icon style={{ height: '20px', width: '20px', color: c.text }} />
          </div>
          <div>
            <div style={{ fontSize: '11px', fontWeight: '600', color: '#8b949e', textTransform: 'uppercase', letterSpacing: '0.8px' }}>{title}</div>
            <div style={{ fontSize: '20px', fontWeight: '700', color: '#e6edf3' }}>{value}</div>
          </div>
        </div>
      </div>
    )
  }

  const LoadingSkeleton = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} style={{ background: '#161b22', borderRadius: '8px', padding: '16px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(8, 1fr)', gap: '16px' }}>
            <div style={{ height: '16px', background: '#21262d', borderRadius: '4px' }}></div>
            <div style={{ height: '16px', background: '#21262d', borderRadius: '4px' }}></div>
            <div style={{ height: '16px', background: '#21262d', borderRadius: '4px' }}></div>
            <div style={{ height: '16px', background: '#21262d', borderRadius: '4px' }}></div>
            <div style={{ height: '16px', background: '#21262d', borderRadius: '4px' }}></div>
            <div style={{ height: '16px', background: '#21262d', borderRadius: '4px' }}></div>
            <div style={{ height: '16px', background: '#21262d', borderRadius: '4px' }}></div>
            <div style={{ height: '16px', background: '#21262d', borderRadius: '4px' }}></div>
          </div>
        </div>
      ))}
    </div>
  )

  return (
    <div style={{ minHeight: '100vh', background: '#0d1117', color: '#e6edf3' }}>
      <div style={{ maxWidth: '1280px', margin: '0 auto', padding: '24px' }}>
        {/* Header */}
        <div style={{ marginBottom: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
            <Fingerprint style={{ height: '32px', width: '32px', color: '#58a6ff' }} />
            <h1 style={{ fontSize: '24px', fontWeight: '700', color: '#e6edf3' }}>
              Fingerprint Details
            </h1>
          </div>
          <p style={{ color: '#8b949e' }}>
            Users associated with fingerprint: <span style={{ color: '#58a6ff', fontFamily: 'monospace' }}>{second_page}</span>
          </p>
        </div>

        {/* Stats Cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '16px', marginBottom: '24px' }}>
          <StatCard
            icon={Users}
            title="Total Users"
            value={users.length}
            color="blue"
          />
          <StatCard
            icon={CheckCircle}
            title="Active Users"
            value={users.filter(u => u.banned === 'False').length}
            color="green"
          />
          <StatCard
            icon={XCircle}
            title="Banned Users"
            value={users.filter(u => u.banned === 'True').length}
            color="red"
          />
          <StatCard
            icon={DollarSign}
            title="Total Earned"
            value={formatCurrency(users.reduce((sum, u) => sum + (parseFloat(u.total_earned) || 0), 0))}
            color="purple"
          />
        </div>

        {/* Search and Table */}
        <div style={{ background: '#161b22', borderRadius: '12px', border: '1px solid #21262d', marginBottom: '24px' }}>
          <div style={{ padding: '16px', borderBottom: '1px solid #21262d' }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', alignItems: 'center' }}>
              <div style={{ position: 'relative', flex: '1', minWidth: '200px' }}>
                <Search style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', height: '16px', width: '16px', color: '#8b949e' }} />
                <input
                  type="text"
                  placeholder="Search by ID, username, or country..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{
                    paddingLeft: '40px',
                    paddingRight: '16px',
                    paddingTop: '8px',
                    paddingBottom: '8px',
                    width: '100%',
                    background: '#0d1117',
                    color: '#e6edf3',
                    borderRadius: '8px',
                    fontSize: '14px',
                    border: '1px solid #30363d',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                  onFocus={(e) => { e.target.style.borderColor = '#58a6ff' }}
                  onBlur={(e) => { e.target.style.borderColor = '#30363d' }}
                />
              </div>
              <div style={{ fontSize: '14px', color: '#8b949e' }}>
                Showing {sortedUsers.length} of {users.length} users
              </div>
            </div>
          </div>

          {/* Table */}
          <div style={{ overflowX: 'auto' }}>
            {loading ? (
              <div style={{ padding: '16px' }}>
                <LoadingSkeleton />
              </div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#161b22', borderBottom: '1px solid #21262d' }}>
                    {[
                      { key: 'id', label: 'ID' },
                      { key: 'username', label: 'USERNAME' },
                      { key: 'country', label: 'COUNTRY' },
                      { key: 'joinDate', label: 'JOIN DATE' },
                      { key: 'balance', label: 'BALANCE' },
                      { key: 'total_earned', label: 'TOTAL EARNED' },
                      { key: 'banned', label: 'STATUS' },
                      { key: 'actions', label: 'ACTIONS' }
                    ].map((column) => (
                      <th
                        key={column.key}
                        style={{
                          padding: '12px 16px',
                          textAlign: 'left',
                          fontSize: '11px',
                          fontWeight: '600',
                          color: '#8b949e',
                          textTransform: 'uppercase',
                          letterSpacing: '0.8px',
                          cursor: column.key !== 'actions' ? 'pointer' : 'default',
                          borderBottom: '1px solid #21262d',
                          whiteSpace: 'nowrap'
                        }}
                        onClick={() => column.key !== 'actions' && handleSort(column.key)}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          {column.label}
                          {sortBy === column.key && (
                            <span style={{ color: '#58a6ff' }}>
                              {sortOrder === 'asc' ? ' ^' : ' v'}
                            </span>
                          )}
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sortedUsers.map((user) => (
                    <tr key={user.id} style={{ borderBottom: '1px solid #21262d', transition: 'background 0.15s' }}
                      onMouseOver={(e) => { e.currentTarget.style.background = 'rgba(33,38,45,0.5)' }}
                      onMouseOut={(e) => { e.currentTarget.style.background = 'transparent' }}
                    >
                      <td style={{ padding: '12px 16px', fontSize: '14px', fontFamily: 'monospace', color: '#c9d1d9' }}>
                        #{user.id}
                      </td>
                      <td style={{ padding: '12px 16px', fontSize: '14px' }}>
                        <div style={{ fontWeight: '500', color: '#e6edf3' }}>{user.username}</div>
                      </td>
                      <td style={{ padding: '12px 16px', fontSize: '14px', color: '#c9d1d9' }}>
                        <span style={{ fontSize: '12px', background: '#21262d', padding: '4px 8px', borderRadius: '4px', color: '#c9d1d9' }}>
                          {user.country}
                        </span>
                      </td>
                      <td style={{ padding: '12px 16px', fontSize: '14px', color: '#c9d1d9' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <Calendar style={{ height: '12px', width: '12px' }} />
                          {formatDate(user.joinDate)}
                        </div>
                      </td>
                      <td style={{ padding: '12px 16px', fontSize: '14px', fontFamily: 'monospace', color: '#3fb950' }}>
                        {formatCurrency(user.balance)}
                      </td>
                      <td style={{ padding: '12px 16px', fontSize: '14px', fontFamily: 'monospace', color: '#58a6ff' }}>
                        {formatCurrency(user.total_earned)}
                      </td>
                      <td style={{ padding: '12px 16px', fontSize: '14px' }}>
                        {getBannedStatus(user.banned)}
                      </td>
                      <td style={{ padding: '12px 16px', fontSize: '14px' }}>
                        <a
                          href={`/pluto/user/${user.id}`}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                            padding: '6px 12px',
                            background: '#238636',
                            border: '1px solid #2ea043',
                            color: '#ffffff',
                            fontSize: '12px',
                            fontWeight: '600',
                            borderRadius: '6px',
                            textDecoration: 'none',
                            transition: 'background 0.15s',
                            cursor: 'pointer'
                          }}
                          onMouseOver={(e) => { e.target.style.background = '#2ea043' }}
                          onMouseOut={(e) => { e.target.style.background = '#238636' }}
                        >
                          <Eye style={{ height: '12px', width: '12px' }} />
                          View User
                        </a>
                      </td>
                    </tr>
                  ))}
                  {sortedUsers.length === 0 && !loading && (
                    <tr>
                      <td colSpan="8" style={{ padding: '32px 16px', textAlign: 'center', color: '#484f58' }}>
                        No users found matching your search criteria
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* Summary */}
        {!loading && users.length > 0 && (
          <div style={{ background: '#161b22', borderRadius: '12px', padding: '16px', border: '1px solid #21262d' }}>
            <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#e6edf3', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <AlertTriangle style={{ height: '20px', width: '20px', color: '#d29922' }} />
              Fingerprint Analysis Summary
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', fontSize: '14px' }}>
              <div>
                <span style={{ color: '#8b949e' }}>Countries detected:</span>
                <span style={{ marginLeft: '8px', color: '#e6edf3', fontWeight: '500' }}>
                  {[...new Set(users.map(u => u.country))].length}
                  {" "}({[...new Set(users.map(u => u.country))].join(', ')})
                </span>
              </div>
              <div>
                <span style={{ color: '#8b949e' }}>Registration span:</span>
                <span style={{ marginLeft: '8px', color: '#e6edf3', fontWeight: '500' }}>
                  {users.length > 0 &&
                    `${Math.ceil((new Date(Math.max(...users.map(u => new Date(u.joinDate)))) -
                      new Date(Math.min(...users.map(u => new Date(u.joinDate))))) / (1000 * 60 * 60 * 24))} days`
                  }
                </span>
              </div>
              <div>
                <span style={{ color: '#8b949e' }}>Avg. earnings:</span>
                <span style={{ marginLeft: '8px', color: '#e6edf3', fontWeight: '500' }}>
                  {formatCurrency(users.reduce((sum, u) => sum + (parseFloat(u.total_earned) || 0), 0) / users.length)}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default PlutoFingerDet
